import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2 } from "lucide-react";

/**
 * Post-checkout landing. Stripe redirects here with ?session_id=cs_...
 * We poll `/api/payments/status/{session_id}` until payment_status is
 * "paid" (or we run out of attempts), then refresh the auth context so
 * the user's is_plus flips true across the app.
 *
 * Poll strategy: 2s interval, max 10 attempts (~20s). Well within Stripe's
 * typical webhook delivery window (usually <5s) but with plenty of buffer
 * for cold-starts or delayed payment methods.
 */
export default function PaymentSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 10;
  const INTERVAL_MS = 2000;

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    async function poll() {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Refresh auth context so is_plus flips true everywhere without a full reload
          await refresh?.();
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        // transient — keep polling within budget
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-md px-6 pt-12 pb-24 text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <Loader2 size={40} className="mx-auto animate-spin text-violet-400" />
          <h1 className="mt-6 font-display text-2xl text-white">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-neutral-400">This usually takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-lg shadow-violet-500/40">
            <Check size={32} className="text-white" />
          </div>
          <h1 className="mt-6 font-display text-3xl text-white" data-testid="payment-success-title">Welcome to Immersive+</h1>
          <p className="mt-2 text-sm text-neutral-400">Your subscription is live. Everything's unlocked.</p>
          <button
            data-testid="payment-success-continue-btn"
            onClick={() => nav("/discover", { replace: true })}
            className="btn-pill btn-primary mt-8 px-6"
          >
            Start exploring
          </button>
        </>
      )}
      {status === "timeout" && (
        <>
          <h1 className="mt-6 font-display text-2xl text-white">Payment still processing</h1>
          <p className="mt-2 text-sm text-neutral-400">
            Your payment is in Stripe's queue. It usually clears in under a minute.
            If your Immersive+ badge doesn't appear within 5 minutes, contact support.
          </p>
          <button onClick={() => nav("/discover", { replace: true })} className="btn-pill btn-ghost mt-8 px-6">
            Continue to app
          </button>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="mt-6 font-display text-2xl text-white">Something went wrong</h1>
          <p className="mt-2 text-sm text-neutral-400">
            We couldn't confirm your payment. If you were charged, contact
            support with the session id below and we'll sort it out.
          </p>
          {sessionId && (
            <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-2 font-mono text-xs text-neutral-500">{sessionId}</div>
          )}
          <button onClick={() => nav("/upgrade", { replace: true })} className="btn-pill btn-primary mt-8 px-6">
            Try again
          </button>
        </>
      )}
    </div>
  );
}
